import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchMovies } from "@/utils/movie";

interface SearchBoxProps {
  defaultValue?: string;
  size?: "sm" | "md";
  autoFocus?: boolean;
  showSuggestions?: boolean;
  placeholder?: string;
  onSubmitKeyword?: (keyword: string) => void;
}

export default function SearchBox({
  defaultValue = "",
  size = "sm",
  autoFocus = false,
  showSuggestions = true,
  placeholder = "搜索电影、导演、演员…",
  onSubmitKeyword,
}: SearchBoxProps) {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setKeyword(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const suggestions = showSuggestions ? searchMovies(keyword, 6) : [];

  function submit(value: string) {
    const trimmed = value.trim();
    setOpen(false);
    if (onSubmitKeyword) {
      onSubmitKeyword(trimmed);
      return;
    }
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : "/search");
  }

  const inputSize = size === "md" ? "h-11 text-sm" : "h-10 text-sm";

  return (
    <div ref={wrapRef} className="relative w-full">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit(keyword);
        }}
        className="flex items-center rounded-full border border-background-300/60 bg-background-100 px-4 transition-colors focus-within:border-primary-400"
      >
        <div className="mr-2 flex h-4 w-4 items-center justify-center text-foreground-400">
          <i className="ri-search-line text-base" />
        </div>
        <input
          type="text"
          value={keyword}
          autoFocus={autoFocus}
          placeholder={placeholder}
          onChange={(event) => {
            setKeyword(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className={`${inputSize} w-full bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 focus:outline-none`}
        />
        {keyword && (
          <button
            type="button"
            aria-label="清空关键词"
            onClick={() => setKeyword("")}
            className="ml-1 flex h-5 w-5 items-center justify-center text-foreground-500 transition-colors hover:text-foreground-200"
          >
            <i className="ri-close-line text-base" />
          </button>
        )}
      </form>

      {open && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-lg border border-background-200/70 bg-background-100 py-2">
          <p className="px-4 pb-1 text-xs text-foreground-500">猜你想看</p>
          {suggestions.map((movie) => (
            <button
              key={movie.id}
              type="button"
              onClick={() => {
                setOpen(false);
                navigate(`/detail/${movie.id}`);
              }}
              className="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-background-200/60"
            >
              <div className="h-12 w-9 flex-shrink-0 overflow-hidden rounded-md bg-background-200">
                <img
                  src={movie.poster}
                  alt={`${movie.title} 海报`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-foreground-50">{movie.title}</p>
                <p className="truncate text-xs text-foreground-500">
                  {movie.year} · {movie.director}
                </p>
              </div>
              <span className="flex-shrink-0 text-sm font-medium text-primary-400">
                {movie.rating.toFixed(1)}
              </span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => submit(keyword)}
            className="mt-1 w-full border-t border-background-200/70 px-4 py-2 text-left text-xs text-primary-400 transition-colors hover:text-primary-300"
          >
            查看「{keyword}」的全部结果
          </button>
        </div>
      )}
    </div>
  );
}